import { Component, type ErrorInfo, type ReactNode } from 'react';
import SentryService from '@services/SentryService';

interface PipErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface PipErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary for PiP portal content. Uses inline styles exclusively
 * because MUI style injection may have failed in the PiP window — inline
 * styles are the only reliable fallback.
 */
export class PipErrorBoundary extends Component<PipErrorBoundaryProps, PipErrorBoundaryState> {
  state: PipErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PipErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    SentryService.captureException(error, {
      tags: { component: 'pip' },
      extra: { componentStack: info.componentStack },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: 16,
            backgroundColor: '#1a1a2e',
            color: 'white',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>Something went wrong</p>
          <button
            onClick={this.props.onClose}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: 4,
            }}
          >
            Return to call
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
